import { useMemo } from 'react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { 
  getFeatureFlagsForUser, 
  canPerformAiAnalysis,
  getUpgradeMessage,
  getPlanDisplayInfo,
  getEffectivePlanId,
  isInActiveTrial,
  getTrialDaysRemaining,
  FeatureFlags 
} from '@/services/featureFlagsService';

/**
 * Hook to access feature flags for the current user
 */
export function useFeatureFlags() {
  const { profile, loading } = useSubscription();
  
  const flags = useMemo(() => {
    if (!profile) return getFeatureFlagsForUser(null);
    
    return getFeatureFlagsForUser({
      subscription_plan_id: profile.subscription_plan_id || profile.subscription_plan,
      analyses_used_this_month: profile.analyses_used_this_month || 0,
      role: profile.role,
      is_in_trial: profile.is_in_trial,
      trial_ends_at: profile.trial_ends_at,
      paid_until: profile.paid_until,
      billing_status: profile.billing_status,
      billing_type: profile.billing_type, // Lifetime > Monthly > Trial
    });
  }, [profile]);
  
  const effectivePlan = useMemo(() => {
    if (!profile) return 'none';
    
    return getEffectivePlanId({
      subscription_plan_id: profile.subscription_plan_id || profile.subscription_plan,
      role: profile.role,
      is_in_trial: profile.is_in_trial,
      trial_ends_at: profile.trial_ends_at,
      paid_until: profile.paid_until,
      billing_status: profile.billing_status,
      billing_type: profile.billing_type,
    });
  }, [profile]);
  
  const analysisCheck = useMemo(() => {
    if (!profile) return canPerformAiAnalysis(null);
    
    return canPerformAiAnalysis({
      subscription_plan_id: profile.subscription_plan_id || profile.subscription_plan,
      analyses_used_this_month: profile.analyses_used_this_month || 0,
      role: profile.role,
      is_in_trial: profile.is_in_trial,
      trial_ends_at: profile.trial_ends_at,
      paid_until: profile.paid_until,
      billing_status: profile.billing_status,
      billing_type: profile.billing_type,
    });
  }, [profile]);
  
  const planInfo = useMemo(() => {
    return getPlanDisplayInfo(effectivePlan);
  }, [effectivePlan]);
  
  const trialInfo = useMemo(() => {
    if (!profile) return { inTrial: false, daysRemaining: null };
    
    const userForFlags = {
      is_in_trial: profile.is_in_trial,
      trial_ends_at: profile.trial_ends_at,
    };
    
    return {
      inTrial: isInActiveTrial(userForFlags),
      daysRemaining: getTrialDaysRemaining(userForFlags),
      trialStartedAt: profile.trial_started_at,
      trialEndsAt: profile.trial_ends_at,
    };
  }, [profile]);
  
  const checkFeature = (feature: keyof FeatureFlags): { allowed: boolean; upgradeMessage?: string } => {
    const value = flags[feature];
    if (typeof value === 'boolean' && !value) {
      return { allowed: false, upgradeMessage: getUpgradeMessage(feature) };
    }
    return { allowed: true };
  };
  
  return {
    flags,
    loading,
    planInfo,
    effectivePlan,
    analysisCheck,
    checkFeature,
    canAnalyze: analysisCheck.allowed,
    analysesRemaining: analysisCheck.remaining,
    trialInfo,
  };
}

export default useFeatureFlags;
